import React from 'react';
import { TrendingUp, TrendingDown, Users, MessageSquare, Calendar, Target } from 'lucide-react';
import { StatCard } from '../ui/StatCard';
import { useAnalytics } from '../../hooks/useAnalytics';

export const Analytics: React.FC = () => {
  const { analytics, loading } = useAnalytics();

  if (loading || !analytics) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-2">Comprehensive insights into appointment attendance patterns</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Overall No-Show Rate"
          value={analytics.noShowRate}
          change={analytics.previousNoShowRate - analytics.noShowRate}
          icon={TrendingDown}
          color="green"
          suffix="%"
        />
        
        <StatCard
          title="Total Appointments"
          value={analytics.totalAppointments}
          change={8.2}
          icon={Calendar}
          color="blue"
        />
        
        <StatCard
          title="Message Delivery Rate"
          value={analytics.messageDeliveryRate}
          change={2.3}
          icon={MessageSquare}
          color="green"
          suffix="%"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">No-Show Rate by Department</h3>
          <div className="space-y-4">
            {analytics.noShowByDepartment.map((dept, index) => (
              <div key={dept.department} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{dept.department}</span>
                    <span className="text-sm text-gray-600">{dept.rate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        dept.rate > 15 
                          ? 'bg-red-500' 
                          : dept.rate > 10 
                            ? 'bg-yellow-500' 
                            : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(dept.rate * 5, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{dept.total} appointments</span>
                    <span>{Math.round(dept.total * dept.rate / 100)} no-shows</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Weekly Trends</h3>
          <div className="space-y-4">
            {analytics.weeklyTrends.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Weekly trends will appear after this clinic uploads appointment data.</p>
            ) : analytics.weeklyTrends.map((week, index) => (
              <div key={week.week} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    Week of {new Date(week.week).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">{week.totalAppointments} appointments</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-lg ${
                    week.noShowRate > 15 
                      ? 'text-red-600' 
                      : week.noShowRate > 10 
                        ? 'text-yellow-600' 
                        : 'text-green-600'
                  }`}>
                    {week.noShowRate}%
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    {index > 0 && analytics.weeklyTrends[index - 1].noShowRate > week.noShowRate ? (
                      <TrendingDown className="w-3 h-3 text-green-500 mr-1" />
                    ) : index > 0 ? (
                      <TrendingUp className="w-3 h-3 text-red-500 mr-1" />
                    ) : null}
                    {index > 0 && (
                      <span>
                        {(week.noShowRate - analytics.weeklyTrends[index - 1].noShowRate).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-gray-700">Low Risk</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.appointmentsByRisk.low}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-gray-700">Medium Risk</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.appointmentsByRisk.medium}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-gray-700">High Risk</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.appointmentsByRisk.high}</span>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-800">
              <Target className="w-5 h-5" />
              <span className="font-medium">Targeting Impact</span>
            </div>
            <p className="text-blue-700 text-sm mt-1">
              Focus on {analytics.appointmentsByRisk.high} high-risk appointments for maximum impact
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Performance</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total Messages Sent</span>
              <span className="font-semibold text-gray-900">{analytics.messagesSent.toLocaleString()}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Delivery Rate</span>
              <span className="font-semibold text-green-600">{analytics.messageDeliveryRate}%</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Avg. per Appointment</span>
              <span className="font-semibold text-gray-900">
                {analytics.totalAppointments > 0
                  ? (analytics.messagesSent / analytics.totalAppointments).toFixed(1)
                  : '0.0'}
              </span>
            </div>
          </div>
          
          {analytics.messagesSent > 0 && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-green-800 font-medium">Message Performance</p>
              <p className="text-green-700 text-sm mt-1">
                Delivery rate is {analytics.messageDeliveryRate >= 89 ? 'above' : 'below'} the industry average of 89%
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
          {analytics.totalAppointments === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">Key insights will appear after this clinic has appointment data.</p>
          ) : (
            <div className="space-y-4">
              {analytics.weeklyTrends.length > 1 && analytics.previousNoShowRate !== analytics.noShowRate && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 font-medium text-sm">
                    No-show rate {analytics.noShowRate < analytics.previousNoShowRate ? 'decreased' : 'increased'} by {Math.abs(analytics.previousNoShowRate - analytics.noShowRate).toFixed(1)}%
                  </p>
                  <p className="text-blue-700 text-xs mt-1">Compared with the previous week</p>
                </div>
              )}
              {analytics.noShowByDepartment.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 font-medium text-sm">
                    {analytics.noShowByDepartment.reduce((highest, department) => department.rate > highest.rate ? department : highest).department} has the highest no-show rate
                  </p>
                  <p className="text-yellow-700 text-xs mt-1">Consider additional reminders</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};